import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { callParkingBoxApi } from "../lib/parkingBoxApi";
import { useAuth } from "../hooks/useAuth";
import { normalizeAuthApiResponse } from "../lib/authResponse";
import { persistAuthCredentials } from "../lib/authCredentials";

const inputClasses = [
  "w-full rounded-xl border px-4 py-3 text-sm transition",
  "border-slate-300 bg-white text-slate-900 placeholder:text-slate-500",
  "focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-300/60",
  "dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-300/70",
  "dark:focus:border-purple-400 dark:focus:ring-purple-500/40",
].join(" ");

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const redirectPath = useMemo(() => {
    const state = location.state as { from?: string } | undefined;
    return state?.from ?? "/";
  }, [location.state]);

  const routeError = useMemo(() => searchParams.get("error") ?? null, [searchParams]);

  useEffect(() => {
    setErrorMessage(routeError);
  }, [routeError]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");
    const remember = formData.get("remember") === "on";

    if (!email || !password) {
      setErrorMessage("Podaj adres e-mail oraz hasło.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const result = await callParkingBoxApi({
        action: "login",
        username: email,
        password,
      });
      const normalized = normalizeAuthApiResponse(result.json);
      const jwtToken = normalized?.token ?? null;
      const statusFlag = normalized?.status ?? null;
      const permissionLevel = normalized?.permission ?? null;
      const userId = normalized?.userId ?? null;

      if (!jwtToken) {
        throw new Error("Błędne dane logowania.");
      }

      if (statusFlag !== 1) {
        throw new Error("Logowanie nie zostało potwierdzone.");
      }

      login(jwtToken, { remember, userEmail: email, userId, permission: permissionLevel });
      persistAuthCredentials({ username: email, password, remember });
      navigate(redirectPath || "/", { replace: true });
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : "Błąd podczas logowania.";
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900 transition-colors dark:bg-neutral-950 dark:text-slate-100 max-[425px]:px-3 max-[425px]:py-10">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-10">
        <div className="flex justify-center text-sm">
          <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs uppercase tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Parking Box
          </span>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.15)] transition-colors dark:border-white/10 dark:bg-neutral-900/80 max-[425px]:p-6">
          <div className="mb-8 space-y-3 text-center">
            <h1 className="text-3xl font-semibold tracking-tight max-[425px]:text-2xl">Logowanie</h1>
          </div>

          {errorMessage ? (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
              {errorMessage}
            </div>
          ) : null}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Adres e-mail
              </label>
              <input id="email" name="email" type="email" autoComplete="email" required className={inputClasses} placeholder="nazwa@mail.com" />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Hasło
              </label>
              <input id="password" name="password" type="password" autoComplete="current-password" required className={inputClasses} placeholder="********" />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-300/80">
              <input
                type="checkbox"
                name="remember"
                className="h-4 w-4 rounded border border-slate-300 bg-white text-purple-600 focus:ring-purple-500 dark:border-white/20 dark:bg-transparent dark:text-purple-300"
              />
              Zapamiętaj mnie
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-xl bg-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-400/30 transition hover:bg-purple-500 disabled:opacity-70 dark:bg-purple-500 dark:hover:bg-purple-400"
            >
              {isSubmitting ? "Logowanie..." : "Zaloguj się"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300/80">
            Nie masz konta?{" "}
            <Link to="/register" className="font-semibold text-purple-600 transition hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300">
              Zarejestruj się
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
